import { useEffect, MutableRefObject } from 'react';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '@lib/firebase';

const BREAK_REQUIRED_AFTER = 2 * 3600;

interface UseTimerSimulatorProps {
  courseId: string;
  userId: string | undefined;
  sessionData: any;
  simulationRefs: MutableRefObject<any>;
  lessonTimeRef: MutableRefObject<number>;
  lastBreakTimeRef: MutableRefObject<number>;
  breakCheckRef: MutableRefObject<boolean>;
  setBreakRequired: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useTimerSimulator = import.meta.env.PROD
  ? () => {}
  : ({
      courseId,
      userId,
      sessionData,
      simulationRefs,
      lessonTimeRef,
      lastBreakTimeRef,
      breakCheckRef,
      setBreakRequired
    }: UseTimerSimulatorProps) => {
      useEffect(() => {
        const isEmulator = import.meta.env.VITE_USE_EMULATORS === 'true';
        const isDev = import.meta.env.DEV;
        const isTest = import.meta.env.VITE_TEST_MODE === 'true';
        const isStaging = import.meta.env.VITE_ENVIRONMENT === 'staging';

        if (isDev || isEmulator || isTest || isStaging) {
          (window as any).fastrackDev = {
            fastForward: (seconds = 60) => {
              console.warn(`[fastrackDev] Fast-forwarding by ${seconds}s`);
              const {
                sessionTimer: st,
                breakManager: bm,
                setBreakTime: sbt,
                setLessonTime: slt,
                saveSessionProgress: ssp
              } = simulationRefs.current;

              st.fastForward(seconds);
              slt((prev: number) => {
                const newVal = prev + seconds;
                lessonTimeRef.current = newVal;
                return newVal;
              });

              // Helper to perform the fast-forward on Firestore active break document if needed
              const syncBreakTimeToFirestore = async () => {
                if (bm.isOnBreak && bm.breakStartTime && userId && sessionData.currentSessionId) {
                  try {
                    const sessionRef = doc(db, 'users', userId, 'sessions', sessionData.currentSessionId);
                    const docSnap = await getDoc(sessionRef);
                    if (docSnap.exists()) {
                      const data = docSnap.data();
                      if (data && data.breaks && data.breaks.length > 0) {
                        const breaks = [...data.breaks];
                        const lastBreak = { ...breaks[breaks.length - 1] };
                        if (lastBreak.status === 'active') {
                          let breakStartMs: number;
                          if (lastBreak.startTime?.toMillis) {
                            breakStartMs = lastBreak.startTime.toMillis();
                          } else if (typeof lastBreak.startTime === 'string') {
                            breakStartMs = new Date(lastBreak.startTime).getTime();
                          } else {
                            breakStartMs = lastBreak.startTime as number;
                          }

                          const newStartMs = breakStartMs - (seconds * 1000);

                          if (lastBreak.startTime?.toMillis) {
                            lastBreak.startTime = Timestamp.fromMillis(newStartMs);
                          } else if (typeof lastBreak.startTime === 'string') {
                            lastBreak.startTime = new Date(newStartMs).toISOString();
                          } else {
                            lastBreak.startTime = newStartMs;
                          }

                          if (lastBreak.initiatedAt) {
                            lastBreak.initiatedAt = new Date(newStartMs).toISOString();
                          }

                          breaks[breaks.length - 1] = lastBreak;
                          await updateDoc(sessionRef, { breaks });
                          console.warn('[fastrackDev] Successfully fast-forwarded the active break in Firestore by:', seconds, 'seconds.');
                        }
                      }
                    }
                  } catch (err) {
                    console.error('[fastrackDev] Failed to update breaks array in Firestore:', err);
                  }
                }
              };

              if (bm.isOnBreak && bm.breakStartTime) {
                bm.setBreakStartTime(bm.breakStartTime - (seconds * 1000));
                // Also update the breakTime state used by the modal
                sbt((prev: number) => Math.max(0, prev - seconds));
                
                // Sync this to Firestore synchronously
                syncBreakTimeToFirestore().then(() => {
                  if (ssp) {
                    ssp().catch((err: any) => console.error('[fastrackDev] ssp failed on break FF:', err));
                  }
                });
                return;
              }

              // Check if the fast-forward pushes us past the 2-hour break threshold, and trigger break immediately
              const newSessionTime = st.sessionTime;
              if (newSessionTime - lastBreakTimeRef.current >= BREAK_REQUIRED_AFTER && !breakCheckRef.current) {
                console.warn('[fastrackDev] 2-hour compliance threshold reached during fast-forward. Auto-triggering startBreak.');
                breakCheckRef.current = true;
                setBreakRequired(true);
                
                // Trigger the break on Firestore synchronously and wait before saving progress
                simulationRefs.current.startBreak().then(() => {
                  if (ssp) {
                    console.warn('[fastrackDev] Forcing immediate Firestore progress sync after break start...');
                    ssp().catch((err: any) => {
                      console.error('[fastrackDev] Failed to force sync session progress:', err);
                    });
                  }
                });
              } else {
                // Force normal progress sync
                if (ssp) {
                  console.warn('[fastrackDev] Forcing immediate Firestore progress sync...');
                  ssp().catch((err: any) => {
                    console.error('[fastrackDev] Failed to force sync session progress:', err);
                  });
                }
              }
            },
            forceBreak: () => {
              console.warn('[fastrackDev] Forcing mandatory break');
              simulationRefs.current.startBreak();
            },
            simulateMandatoryBreak: async () => {
              console.warn('[fastrackDev] Simulating mandatory break');
              await simulationRefs.current.startBreak();
            },
            simulateEndBreak: async () => {
              console.warn('[fastrackDev] Simulating end break');
              await simulationRefs.current.endBreak();
            },
            simulateDailyLimit: async () => {
              console.warn('[fastrackDev] Simulating daily limit reached');
              await simulationRefs.current.sessionTimer.checkDailyLockout(() => Promise.resolve(true));
            },
            simulateInactivityWarning: () => {
              console.warn('[fastrackDev] Simulating inactivity warning');
              const oldTime = Date.now() - (14 * 60 * 1000);
              simulationRefs.current.activityTracking.simulateActivityTime(oldTime);
              // Dispatch custom event to trigger warning inside useInactivityTimeout hook
              window.dispatchEvent(new CustomEvent('fastrack:trigger-inactivity-warning'));
            },
            simulateInactivity: async () => {
              console.warn('[fastrackDev] Simulating inactivity timeout');
              const oldTime = Date.now() - (16 * 60 * 1000);
              simulationRefs.current.activityTracking.simulateActivityTime(oldTime);
            },
            resetDailyLimit: () => {
              console.warn('[fastrackDev] Resetting daily limit');
              simulationRefs.current.sessionTimer.loadDailyTime(0);
            },
            inspectTimerState: () => {
              const {
                sessionTimer: st,
                breakManager: bm,
                sessionData: sd
              } = simulationRefs.current;
              console.warn('[fastrackDev] Current Timer State:', {
                sessionTime: st.sessionTime,
                totalTime: st.totalTime,
                isActive: st.isActive,
                isPaused: st.isPaused,
                isLockedOut: st.isLockedOut,
                breakRequired: st.breakRequired,
                isOnBreak: bm.isOnBreak,
                isBreakDue: bm.isBreakDue,
                currentSessionId: sd.currentSessionId,
              });
            },
            simulateOfflinePending: (count: number) => {
              console.warn(`[fastrackDev] Simulating offline pending retries: ${count}`);
            },
            unlockLesson: (lessonId: string) => {
              console.warn(`[fastrackDev] Unlocking lesson: ${lessonId}`);
              if (simulationRefs.current.sessionData.currentSessionId) {
                simulationRefs.current.sessionData.recordLessonAccess(lessonId);
              } else {
                console.error('[fastrackDev] Cannot unlock lesson: No active session.');
              }
            }
          };

          console.warn('🚀 Fastrack Developer Tools loaded: window.fastrackDev');
        }

        return () => {
          if (typeof window !== 'undefined' && (window as any).fastrackDev) {
            delete (window as any).fastrackDev;
          }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [courseId, userId]);
    };
